<template>
  <b-overlay :show="isLoading" bg-color="transparent" blur="2px">
    <!-- LOADING OVERLAY -->
    <template #overlay>
      <img src="@/assets/lottie/plant.gif" style="width: 5em" />
    </template>
    <b-card no-body class="pt-5 rounded-xl mb-5">
      <!-- CARD HEADER -->
      <b-card-header
        class="border-2 border-bottom px-5 pt-0 pb-3 d-flex align-items-center"
        v-if="title"
      >
        <a
          v-if="!$store.state.unknownRoute && showBack"
          @click="$router.go(-1)"
          class="btn btn-transparent btn-lg btn-icon btn-back mr-1"
          v-b-tooltip.top="$t('global.go_back')"
        >
          <i class="ri-arrow-left-circle-line text-primary p-0"></i>
        </a>
        <b-card-title v-if="title" class="mb-0">
          {{ title }}
        </b-card-title>
        <div class="ml-auto"></div>
        <b-btn
          v-if="showEdit"
          class="py-2"
          size="sm"
          variant="light-warning"
          @click="$emit('onEdit')"
        >
          <i class="ri-edit-line"></i>
          Edit
        </b-btn>
        <b-btn
          v-if="showDelete"
          class="ml-2 py-2"
          size="sm"
          variant="light-danger"
          @click="$emit('onDelete')"
        >
          <i class="ri-delete-bin-line"></i>
          Hapus
        </b-btn>
      </b-card-header>
      <!-- CARD BODY -->
      <b-card-body class="pt-5 pb-0">
        <slot name="card-body">
          <!-- get from api component -->
          <div v-if="api">
            <b-table
              :fields="tempFields"
              :items="table.data"
              :per-page="table.perPage"
              :current-page="table.currentPage"
              @filtered="onTableFiltered"
            >
              <template v-for="(field, fIndex) in tempFields" v-slot:[`cell(${field.key})`]="{ item, index }">
                
                <span v-if="field.key === 'index'">
                  {{ index + (table.currentPage - 1) * table.perPage + 1 }}.
                </span>

                <span v-else-if="field.type === 'image'">
                  <img
                    v-if="item[field.key]"
                    class="img-table"
                    v-img-fb="item[field.key].url || item[field.key]"
                    :alt="field.label"
                  />
                </span>

                <span v-else-if="field.type === 'map'">
                  <base-map-coordinate-loader
                    :title="field.label"
                    :coordinate_data="item[field.key]"
                  />
                </span>

                <span v-else>
                  <slot
                    v-if="$scopedSlots[field.key]"
                    :name="field.key"
                    :rowData="item"
                  ></slot>
                  <template v-else>
                    {{ item[field.key] }}
                  </template>
                </span>
              </template>
            </b-table>
            <b-pagination
              v-if="!hidePagination"
              :total-rows="table.totalRows"
              :per-page="table.perPage"
              v-model="table.currentPage"
            ></b-pagination>
          </div>
          <!-- default type -->
          <div v-else>
            <table v-if="!isLoading" class="table table-detail">
              <tbody>
                <tr
                  v-for="(field, fIndex) in tempFields"
                  :key="`row-detail-${fIndex}`"
                >
                  <td class="nowrap-table">
                    {{ field.label }}
                  </td>
                  <td class="nowrap-table">&emsp;:&nbsp;</td>
                  <!-- image type -->
                  <td v-if="field.type == 'image'" class="">
                    <img
                      class="img-table"
                      v-img-fb="
                        mainData[field.key] ? mainData[field.key].url : ''
                      "
                      :alt="field.label"
                    />
                  </td>
                  <td v-else-if="field.type == 'map'" class="">
                    <base-map-coordinate-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                    />
                  </td>
                  <td v-else-if="field.type == 'single-polygon'" class="">
                    <base-single-polygon-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                    />
                  </td>
                  <td v-else-if="field.type == 'multi-polygon'" class="">
                    <base-multi-polygon-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                      :polygons_data="getPolygonsData(field)"
                    />
                  </td>
                  <td v-else class="detail-value-cell">
                    <div class="detail-value-wrapper">
                      <slot
                        v-if="$scopedSlots[field.key]"
                        :name="field.key"
                        :rowData="mainData"
                      ></slot>

                      <template v-else>
                        <span :class="classReader(mainData[field.key], field.classes)">
                          <span v-if="field.transform">
                            {{ mainData[field.key] | parse(field.transform) }}
                          </span>
                          <span v-else>
                            {{ mainData[field.key] }}
                          </span>
                        </span>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </slot>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
export default {
  name: "GrowerWebCardDetail",

  props: {
    title: {
      type: String,
      default: "",
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "",
    },
    coordinate: {
      type: Object,
      default: () => ({}),
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    api: {
      type: String,
      default: "",
    },
    apiParams: {
      type: Object,
      default: () => ({}),
    },
    hideIndex: {
      type: Boolean,
      default: false,
    },
    hidePagination: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    tempFields() {
      if (this.fields.length) {
        const newFields = [];
        if (this.api && !this.hideIndex) {
          newFields.push({
          label: "No",
          key: "index",
          thStyle: { width: '60px', textAlign: 'center' },
          tdClass: 'text-center',
        });
        }

        newFields.push(...this.fields);

        return newFields;
      } else {
        const newFields = [];
        for (const [field, val] of Object.entries(this.data)) {
          newFields.push({
            label: field,
            key: field,
          });
        }
        return newFields;
      }
    },
  },


  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.mainData = {
            ...this.mainData,
            ...val,
          };
        }
      },
    },
  },

  data() {
    return {
      isLoading: false,
      mainData: {},
      table: {
        data: [],
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
      },
    };
  },

  mounted() {
    if (this.api) {
      this.getData();
    }
  },

  methods: {
    classReader(value, classes) {
      if (classes instanceof Object) {
        if (value instanceof Array) {
          value.forEach((val) => {
            return classes[val];
          });
        } else return classes[value];
      } else return classes;
    },
    getCoordinateData(field) {
      if (field.coordinate && this.mainData) {
        const latKey = field.coordinate.latitude;
        const lonKey = field.coordinate.longitude;

        return {
          latitude: this.mainData[latKey] || 0,
          longitude: this.mainData[lonKey] || 0,
        };
      }
      return { latitude: 0, longitude: 0 };
    },
    getPolygonsData(field) {
      if (field.polygon && this.mainData) {
        const polygonKey = field.polygon;
        const polygons = this.mainData[polygonKey];

        if (Array.isArray(polygons) && polygons.length > 0) {
          return polygons;
        }
      }

      return [];
    },
    getData: async function () {
      try {
        this.isLoading = true;

        const res = await this.$_api.get(this.api, this.apiParams);
        this.table.data = res.data;
        this.table.totalRows = this.table.data.length;
      } catch (err) {
        console.log("getData() error", err);
        this.$_alert.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    onTableFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.table.totalRows = filteredItems.length;
      this.table.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.b-card-body {
  overflow-x: hidden;
}

.table-detail td:first-child {
  width: 240px;
  white-space: nowrap;
  vertical-align: top;
}

.detail-value-cell {
  max-width: 100%;
  vertical-align: top;
}

.detail-value-wrapper {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: normal;
}

.detail-value-wrapper > * {
  max-width: 100%;
}

.img-table {
  height: 150px;
  width: 150px;
  min-width: 150px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  margin-right: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/base/component/card-detail.vue